import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  downloadBlob,
  downloadClientExcelModel,
  exportClientsExcel,
  importClientsExcel,
  validateClientsExcel,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import PageHeader from '../common/PageHeader';

export default function ClientExcelPage() {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [validacao, setValidacao] = useState(null);
  const [result, setResult] = useState(null);

  const busy = loading || importing;
  const errosValidacao = validacao?.erros ?? [];
  const errosResultado = result?.errosDetalhados ?? [];

  const run = async (action) => {
    setLoading(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha na operação Excel.'));
    } finally {
      setLoading(false);
    }
  };

  const importar = async (token) => {
    setImporting(true);
    setResult(null);
    setError('');
    try {
      const { data } = await importClientsExcel({ token });
      setResult(data);
      setValidacao(null);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Falha na importação.'));
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Excel de clientes"
        subtitle="Baixe o modelo, exporte o cadastro atual ou importe planilha (.xlsx). Id = 0 cria novo; Id > 0 atualiza; sem Id, atualiza pelo CNPJ/CPF."
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={busy}
            onClick={() =>
              run(async () => {
                const response = await downloadClientExcelModel();
                downloadBlob(response.data, 'ModeloClientes.xlsx');
              })
            }
          >
            Baixar modelo
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={busy}
            onClick={() =>
              run(async () => {
                const response = await exportClientsExcel();
                downloadBlob(
                  response.data,
                  `CadastroClientes_${new Date().toISOString().slice(0, 10)}.xlsx`,
                );
              })
            }
          >
            Exportar cadastro
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            Selecionar Excel
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              run(async () => {
                setValidacao(null);
                setResult(null);
                const response = await validateClientsExcel(file);
                setValidacao(response.data);
              });
            }}
          />
        </Stack>
      </Paper>

      {(loading || importing) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
          {importing && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Importando clientes…
            </Typography>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {validacao && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Alert
            severity={validacao.token ? (errosValidacao.length ? 'warning' : 'success') : 'error'}
            sx={{ mb: 2 }}
          >
            {validacao.token ? (
              <>
                Arquivo lido com sucesso.
                <br />
                Clientes: {validacao.totalLinhas} linha(s) · válidas: {validacao.linhasValidas}
                {errosValidacao.length > 0 && (
                  <>
                    <br />
                    {errosValidacao.length} aviso(s) encontrado(s) — linhas com erro serão
                    ignoradas na importação.
                  </>
                )}
              </>
            ) : (
              <>Não foi possível ler o arquivo. Corrija os erros abaixo e tente novamente.</>
            )}
          </Alert>

          {errosValidacao.length > 0 && (
            <TableContainer sx={{ maxHeight: 320, mb: 2 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Aba</TableCell>
                    <TableCell width={80}>Linha</TableCell>
                    <TableCell>Mensagem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errosValidacao.map((erro, idx) => (
                    <TableRow key={`${erro.aba}-${erro.linha}-${idx}`}>
                      <TableCell>{erro.aba}</TableCell>
                      <TableCell>{erro.linha > 0 ? erro.linha : '—'}</TableCell>
                      <TableCell>{erro.mensagem}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {validacao.token && (
            <Button
              variant="contained"
              color="primary"
              disabled={busy || !validacao.totalLinhas}
              onClick={() => importar(validacao.token)}
            >
              Importar {validacao.totalLinhas} cliente(s)
            </Button>
          )}
        </Paper>
      )}

      {result && (
        <Paper sx={{ p: 2 }}>
          <Alert severity={errosResultado.length ? 'warning' : 'success'} sx={{ mb: 2 }}>
            Importação concluída — criados: {result.criados} · atualizados: {result.atualizados}
            {errosResultado.length > 0 && (
              <>
                <br />
                {errosResultado.length} linha(s) com erro (demais linhas foram importadas).
              </>
            )}
          </Alert>
          {errosResultado.length > 0 && (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Aba</TableCell>
                    <TableCell width={80}>Linha</TableCell>
                    <TableCell>Mensagem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errosResultado.map((erro, idx) => (
                    <TableRow key={`${erro.aba}-${erro.linha}-${idx}`}>
                      <TableCell>{erro.aba}</TableCell>
                      <TableCell>{erro.linha > 0 ? erro.linha : '—'}</TableCell>
                      <TableCell>{erro.mensagem}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}
